import React, { useEffect, useState } from 'react';
import { Card, Typography, Form, Input, Button, message, Divider, Alert, Modal, Spin, Popover } from 'antd';
import { SaveOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/AppRedux/reducers';
import { hotelProfileContentRequest, requestHotelProfileGoogleMapUpdate, clearHotelProfileProcess, processAction } from "@/AppRedux/actions/admin/hotelProfileConfig";
const { Title, Text } = Typography;

const GoogleMap: React.FC = () => {
  const [form] = Form.useForm();
  const [savedMapUrl, setSavedMapUrl] = useState<string>('');
  const [savedMapShareUrl, setSavedMapShareUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const hotelProfileConfig = useSelector((state: RootState) => state.hotelProfileConfig);

  useEffect(() => {
    if (!hotelProfileConfig.dataSource)
      dispatch(hotelProfileContentRequest());
  }, [dispatch]);

  useEffect(() => {
    if (hotelProfileConfig.process.action && processAction.includes(hotelProfileConfig.process.action)) {

      if (hotelProfileConfig.process.action === "HOTEL_PROFILE_GOOGLE_MAP_UPDATE") {

        if (hotelProfileConfig.process.status)
          message.success(hotelProfileConfig.process.message)
        else
          Modal.error({
            title: 'Error',
            content: hotelProfileConfig.process.message || 'An error occurred while processing.',
          });
      }
      dispatch(clearHotelProfileProcess());
      setTimeout(() => {
        setLoading(false);
      }, 1000);

    }
  }, [hotelProfileConfig.process, dispatch]);

  useEffect(() => {
    if (hotelProfileConfig.dataSource) {
      const { map_url, map_iframe, map_url_embed } = hotelProfileConfig.dataSource;
      setSavedMapUrl(map_url_embed || '');
      setSavedMapShareUrl(map_url || '');
      form.setFieldsValue({ mapIframe: map_iframe, mapShareUrl: map_url });
    }
  }, [hotelProfileConfig]);

  const onFinish = (values: { mapIframe: string, mapShareUrl: string }) => {
    setLoading(true);
    const urlToSave = values.mapIframe ? values.mapIframe.trim() : '';
    const shareUrlToSave = values.mapShareUrl ? values.mapShareUrl.trim() : '';
    dispatch(requestHotelProfileGoogleMapUpdate({ mapIframe: urlToSave, mapShareUrl: shareUrlToSave }));
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Card>
        <Spin spinning={loading || hotelProfileConfig.loading}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Title level={2} style={{ margin: 0 }}>Google Map Setup</Title>
          </div>

          <Text type="secondary">
            Manage the Google Map embed URL for the hotel location and a shareable link for confirmation emails.
          </Text>

          <Divider />

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ mapIframe: '', mapShareUrl: '' }}
          >
            <Form.Item
              name="mapIframe"
              label={
                <span>
                  Google Maps Embed URL&nbsp;
                  <Popover
                    trigger="click"
                    title={<strong>How to get the Embed URL</strong>}
                    styles={{ root: { maxWidth: 360 } }}
                    content={
                      <ol style={{ paddingLeft: 16, margin: 0 }}>
                        <li>Go to <a href="https://maps.google.com" target="_blank" rel="noreferrer">maps.google.com</a></li>
                        <li>Search for your hotel location</li>
                        <li>Click the <strong>Share</strong> button on the left panel</li>
                        <li>Select the <strong>"Embed a map"</strong> tab</li>
                        <li>Copy the full HTML code containing <code>&lt;iframe src="..."&gt;&lt;/iframe&gt;</code></li>
                        <li>Paste it into this field</li>
                      </ol>
                    }
                  >
                    <QuestionCircleOutlined style={{ color: '#1677ff', fontSize: 14, cursor: 'pointer' }} />
                  </Popover>
                </span>
              }
              extra="Example: <iframe src='https://www.google.com/maps/embed?pb=!1m18...'>...</iframe>"
            >
              <Input.TextArea
                rows={4}
                placeholder="Paste your Google Maps Embed URL here"
                allowClear
              />
            </Form.Item>

            <Form.Item
              name="mapShareUrl"
              label={
                <span>
                  Google Maps Share Link (for Email)&nbsp;
                  <Popover
                    trigger="click"
                    title={<strong>How to get the Share Link</strong>}
                    styles={{ root: { maxWidth: 360 } }}
                    content={
                      <ol style={{ paddingLeft: 16, margin: 0 }}>
                        <li>Go to <a href="https://maps.google.com" target="_blank" rel="noreferrer">maps.google.com</a></li>
                        <li>Search for your hotel location</li>
                        <li>Click the <strong>Share</strong> button on the left panel</li>
                        <li>Select the <strong>"Send a link"</strong> tab</li>
                        <li>Copy the short URL (e.g. <code>https://maps.app.goo.gl/...</code>)</li>
                        <li>Paste it into this field</li>
                      </ol>
                    }
                  >
                    <QuestionCircleOutlined style={{ color: '#1677ff', fontSize: 14, cursor: 'pointer' }} />
                  </Popover>
                </span>
              }
              extra="Optional: Provide a shareable Google Maps link for booking confirmation emails. Example: https://maps.app.goo.gl/..."
            >
              <Input
                placeholder="Paste your Google Maps Share Link here"
                allowClear
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                Save Configuration
              </Button>
            </Form.Item>

          </Form>

          <Divider />
          {savedMapUrl ? (
            <div style={{
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid #f0f0f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <iframe
                src={savedMapUrl}
                width="100%"
                height="450"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Map Preview"
              />
            </div>
          ) : (
            <Alert
              title="ยังไม่มีการ setting google map"
              description="Please enter a valid Google Maps Embed URL and save to see the preview."
              type="warning"
              showIcon
            />
          )}
        </Spin>
      </Card>
    </div>
  );
}
export default GoogleMap;